import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  setSnackBar,
  showLoadingScreen,
} from "../../reusable_components/site_data/siteDataSlice";
import { backend_url } from "../../App";

export const organizationSlice = createSlice({
  name: "organization",
  initialState: {
    organization: {
      name: "",
      cameras: [],
      species: [],
      can_annotate: false,
      can_unannotate: false,
      can_feature: false,
      can_delete: false,
    },
  },
  reducers: {
    setOrganization: (state, action) => {
      state.organization = action.payload;
    },
    clearOrganization: (state) => {
      state.organization = null;
    },
  },
});

// Action creators
export const { setOrganization, clearOrganization } = organizationSlice.actions;

// Helper function to fetch ALL cameras (paginate through all pages)
const fetchAllCameras = async (config) => {
  let allCameras = [];
  let nextUrl = `${backend_url}/core/api/camera/?page_size=100`;

  while (nextUrl) {
    try {
      const response = await axios.get(nextUrl, config);
      const data = response.data;
      allCameras = [...allCameras, ...(data.results || [])];
      nextUrl = data.next;
    } catch (error) {
      console.error("Error fetching cameras:", error);
      break;
    }
  }

  return allCameras;
};

// Thunk to fetch organization data
export const getOrganization = () => async (dispatch) => {
  try {
    dispatch(showLoadingScreen(true));

    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!token) {
      throw new Error("User is not authenticated");
    }

    const config = {
      headers: {
        Authorization: `Token ${token}`,
      },
    };

    // Fetch organization details
    const orgResponse = await axios.get(
      `${backend_url}/core/api/organization/`,
      config,
    );
    const orgData = orgResponse.data;

    console.log("Organization data:", orgData);

    let cameras = [];
    let species = [];

    // Admin sees all cameras and species
    if (user.user_type === "admin") {
      cameras = await fetchAllCameras(config);

      const speciesRes = await axios.get(
        `${backend_url}/core/api/specie/`,
        config,
      );
      species = speciesRes.data.results || speciesRes.data || [];
    }
    // Org admin sees all cameras and species in their organization
    else if (user.user_type === "superuser" && user.organization) {
      const orgId =
        typeof user.organization === "object"
          ? user.organization.id
          : user.organization;

      console.log("Org Admin - Organization ID:", orgId);
      console.log("User organization:", user.organization);

      // Fetch ALL cameras (paginate through all pages)
      const allCameras = await fetchAllCameras(config);
      console.log("All cameras fetched:", allCameras.length);

      // Filter cameras by organization
      cameras = allCameras.filter((cam) => {
        // Check if camera's organization matches
        const camOrg =
          typeof cam.organization === "object"
            ? cam.organization?.id
            : cam.organization;
        return camOrg === orgId;
      });
      console.log("Filtered cameras for org:", cameras.length);
      console.log(
        "Camera organization IDs:",
        allCameras.map((c) =>
          typeof c.organization === "object"
            ? c.organization?.id
            : c.organization,
        ),
      );

      // Fetch organization species permissions
      const orgSpeciesRes = await axios.get(
        `${backend_url}/core/api/organization-species-permissions/?organization=${orgId}`,
        config,
      );
      const orgSpeciesPerms =
        orgSpeciesRes.data.results || orgSpeciesRes.data || [];

      // Fetch full species details
      const speciesPromises = orgSpeciesPerms.map(async (perm) => {
        const specieId =
          typeof perm.specie === "object" ? perm.specie.id : perm.specie;
        try {
          const specieRes = await axios.get(
            `${backend_url}/core/api/specie/${encodeURIComponent(specieId)}/`,
            config,
          );
          return specieRes.data;
        } catch (err) {
          console.error(`Error fetching species ${specieId}:`, err);
          return null;
        }
      });
      species = (await Promise.all(speciesPromises)).filter((s) => s !== null);
    }
    // Regular user sees only their assigned cameras and species
    else {
      // Fetch user's camera species permissions
      const cameraSpeciesRes = await axios.get(
        `${backend_url}/core/api/camera-species-permissions/?user=${user.id}`,
        config,
      );
      const cameraSpeciesPerms =
        cameraSpeciesRes.data.results || cameraSpeciesRes.data || [];

      // Get assigned camera IDs
      const assignedCameraIds = cameraSpeciesPerms.map((perm) =>
        typeof perm.camera === "object" ? perm.camera.id : perm.camera,
      );

      // Fetch ALL cameras, then filter to assigned ones
      const allCameras = await fetchAllCameras(config);
      cameras = allCameras.filter((cam) => assignedCameraIds.includes(cam.id));

      // Get assigned species IDs from camera species permissions
      const assignedSpeciesIds = cameraSpeciesPerms.map((perm) =>
        typeof perm.species === "object" ? perm.species.id : perm.species,
      );

      // Also get user species permissions
      const userSpeciesRes = await axios.get(
        `${backend_url}/core/api/user-species-permissions/?user=${user.id}`,
        config,
      );
      const userSpeciesPerms =
        userSpeciesRes.data.results || userSpeciesRes.data || [];

      const userAssignedSpeciesIds = userSpeciesPerms.map((perm) =>
        typeof perm.specie === "object" ? perm.specie.id : perm.specie,
      );

      // Combine all species IDs and remove duplicates
      const allSpeciesIds = [
        ...new Set([...assignedSpeciesIds, ...userAssignedSpeciesIds]),
      ].filter((id) => id);

      // Fetch full species details
      const speciesPromises = allSpeciesIds.map(async (specieId) => {
        if (!specieId) return null;
        try {
          const specieRes = await axios.get(
            `${backend_url}/core/api/specie/${encodeURIComponent(specieId)}/`,
            config,
          );
          return specieRes.data;
        } catch (err) {
          console.error(`Error fetching species ${specieId}:`, err);
          return null;
        }
      });
      species = (await Promise.all(speciesPromises)).filter((s) => s !== null);
    }

    // Update organization data with filtered cameras and species
    const updatedOrgData = {
      ...orgData,
      cameras: cameras,
      species: species,
    };

    console.log("Final organization data:", updatedOrgData);
    dispatch(setOrganization(updatedOrgData));
  } catch (error) {
    console.error("Error fetching organization:", error);
    dispatch(setSnackBar(error.message));
  } finally {
    dispatch(showLoadingScreen(false));
  }
};

// Selector to get organization state
export const selectOrganization = (state) => state.organization.organization;

// Default export
export default organizationSlice.reducer;
